import isDeepLink from 'owa-url/lib/isDeepLink';
import { initTableSelectionOnLoad } from 'owa-mail-actions/lib/initTableSelectionOnLoad';
import { singleSelectRow } from 'owa-mail-actions/lib/singleSelectRow';
import type { TableView } from 'owa-mail-list-store';
import {
    getSelectedTableView,
    type LoadErrorStatus,
    type TableQueryType,
} from 'owa-mail-list-store';
import { onInitialTableLoadComplete as onInitialTableLoadCompleteAction } from 'owa-mail-triage-table-utils';
import { prefetchFirstN } from 'owa-mail-prefetch';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import { mutatorAction } from 'satcheljs';

const FIRST_N_ROW_PREFETCH_COUNT = 10;

/**
 * Callback for table load complete
 * @param tableView the table view
 * @param isSuccessResponseClass indicates if the response is success
 * @param isTablePrefetched indicates if the table is prefetched
 */
export default function onInitialTableLoadComplete(
    tableView: TableView,
    isSuccessResponseClass: boolean,
    responseStatusMessage: string,
    isTablePrefetched: boolean
): void {
    // To avoid making unnessessary service calls, only prefetch top N if the initial load has not already completed or if the previous load ended in error
    const shouldPrefetchTopN = !tableView.isInitialLoadComplete || tableView.loadErrorStatus != 0;

    onInitialTableLoadCompleteMutator(
        tableView,
        isSuccessResponseClass,
        responseStatusMessage,
        isTablePrefetched
    );

    if (isTablePrefetched) {
        return;
    }

    // If the response class is success then prefetch and perform auto selection
    if (isSuccessResponseClass) {
        const hasSelection = tableView.selectedRowKeys.size > 0;

        // do auto selection and prefetch first N rows for Folder and Group tables
        if (
            tableView.tableQuery.type == 0 ||
            tableView.tableQuery.type == 2 ||
            tableView.tableQuery.type == 3
        ) {
            if (tableView == getSelectedTableView()) {
                if (!hasSelection) {
                    // Init table selection if selected table doesn't already have any selected rows
                    initTableSelectionOnLoad(tableView);
                } else {
                    // The selection may have been pre-seeded with a rowId (ConversationId/ItemId)
                    // rather than a rowKey (InstanceKey) to preserve selection after a sort change
                    // and prevent autoSelectFirstRow from skipping pinned READ items.
                    // After loading, if the seeded key is not a valid rowKey in the current table,
                    // resolve it to the actual InstanceKey via rowIdToRowKeyMap.
                    const preservedKey = [...tableView.selectedRowKeys.keys()][0];
                    if (tableView.rowKeys.indexOf(preservedKey) < 0) {
                        const resolvedRowKeys = tableView.rowIdToRowKeyMap.get(preservedKey);
                        const validRowKey = resolvedRowKeys?.find(
                            rk => tableView.rowKeys.indexOf(rk) >= 0
                        );
                        if (validRowKey) {
                            singleSelectRow(
                                tableView,
                                validRowKey,
                                false /* isUserNavigation */,
                                MailListItemSelectionSource.Auto
                            );
                        } else {
                            // The preserved row is not in the current page — fall back to auto-select.
                            initTableSelectionOnLoad(tableView);
                        }
                    }
                }
            }

            if (shouldPrefetchTopN && !isDeepLink()) {
                prefetchFirstN(tableView, FIRST_N_ROW_PREFETCH_COUNT);
            }
        }

        if (tableView.tableQuery.type == 1) {
            // Queue the work to guarantee that focus has been reset before updating
            // selection in the table so that the focus is not lost.
            setTimeout(() => {
                // Initialize table selection
                initTableSelectionOnLoad(tableView);
            }, 0);
        }

        onInitialTableLoadCompleteAction(tableView);
    }
}

const onInitialTableLoadCompleteMutator = mutatorAction(
    'onInitialTableLoadComplete',
    (
        tableView: TableView,
        isSuccessResponseClass: boolean,
        responseStatusMessage: string,
        isTablePrefetched: boolean
    ) => {
        // Set loading state to complete
        tableView.isLoading = false;

        // Just set the isInitialLoadComplete flag to true if the response class is success
        tableView.isInitialLoadComplete = isSuccessResponseClass;

        if (isTablePrefetched) {
            return;
        }

        // If the response class is success then prefetch and perform auto selection
        if (isSuccessResponseClass) {
            tableView.loadErrorStatus = 0;
        } else {
            setTableViewErrorState(tableView, responseStatusMessage);
        }
    }
);

/**
 * Callback for set tableview error state
 * @param tableView the table view
 * @param responseStatusMessage indicates the error response status
 */
export const setTableViewErrorStateMutator: typeof setTableViewErrorState = mutatorAction(
    'setTableViewErrorState',
    setTableViewErrorState
);

function setTableViewErrorState(tableView: TableView, responseStatusMessage: string): void {
    switch (responseStatusMessage) {
        case 'ErrorAccessDenied':
            tableView.loadErrorStatus = 1;
            break;
        case 'UnexpectedClientError':
            tableView.loadErrorStatus = 2;
            break;
        case 'NetworkError':
            tableView.loadErrorStatus = 3;
            break;
        default:
            tableView.loadErrorStatus = 4;
    }
}
